
// src/components/Footer.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  InstagramIcon, FacebookIcon, PhoneIcon, MailIcon, LinkedinIcon,
  Twitter, Youtube, Clock, MapPin, Globe, ExternalLink, Home, 
  Package, Users, FileText, MessageCircle
} from 'lucide-react'; 

// Social media icon mapping
const socialIconMap = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  linkedin: LinkedinIcon,
  twitter: Twitter,
  youtube: Youtube
};

const Footer = ({ menuName = 'footer' }) => {
  const [menuData, setMenuData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);
  const [footerOptions, setFooterOptions] = useState({});
  const [customStyles, setCustomStyles] = useState({});
  const location = useLocation();
  const isAdminDashboard = location.pathname === '/pitech';

  useEffect(() => {
    const fixAdminDashboardPadding = () => {
      // Fix for admin dashboard
      if (isAdminDashboard) {
      
        const footer = document.querySelector('footer');
        if (footer) {
          footer.style.display = 'none'; // Fixed height for navbar on admin dashboard
       }
      }
    };
    fixAdminDashboardPadding();
  });

  // Fetch menu data
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const response = await axios.get(`/api/menus/name/${menuName}`);
        const menu = response.data;
        
        setMenuData(menu);
        // Sort menu items by order property before setting state
        if (menu.items && menu.items.length > 0) {
          const sortedItems = [...menu.items].sort((a, b) => {
            // If order properties are the same, maintain original order
            if (a.order === b.order) return 0;
            // Otherwise sort by order property
            return a.order - b.order;
          });
          
          // Also sort children for each item
          sortedItems.forEach(item => {
            if (item.children && item.children.length > 0) {
              item.children.sort((a, b) => a.order - b.order);
            }
          });
          
          setMenuItems(sortedItems);
        } else {
          setMenuItems([]);
        }

        // Extract styling information
        if (menu.styles) {
          setCustomStyles(menu.styles);
        }
        
        
        if (menu.footerOptions) {
          setFooterOptions(menu.footerOptions);
        }
        
        // Load custom CSS if provided
        if (menu.customCss) {
          // Create a style element for custom CSS
          const styleElement = document.createElement('style');
          styleElement.id = `footer-custom-css-${menu._id}`;
          styleElement.textContent = menu.customCss;
          document.head.appendChild(styleElement);
          
          // Cleanup function
          return () => {
            const existingStyle = document.getElementById(`footer-custom-css-${menu._id}`);
            if (existingStyle) {
              document.head.removeChild(existingStyle);
            }
          };
        }
      } catch (error) {
        console.error('Error fetching footer menu:', error);
        // If we can't fetch the footer menu, we'll use default values
        setMenuLoading(false);
      } finally {
        setMenuLoading(false);
      }
    };
    
    fetchMenu();
  }, [menuName]);
  
  // Generate footer style
  const getFooterStyle = () => {
    const styles = {};
    
    if (customStyles.backgroundColor) {
      styles.backgroundColor = customStyles.backgroundColor;
    }
    
    if (customStyles.textColor) {
      styles.color = customStyles.textColor;
    }
    
    if (customStyles.fontFamily) {
      styles.fontFamily = customStyles.fontFamily;
    }
    
    return styles;
  };
  
  // Generate link style
  const getLinkStyle = (isHovered = false) => {
    const styles = {};
    
    if (isHovered && customStyles.hoverTextColor) {
      styles.color = customStyles.hoverTextColor;
    } else if (customStyles.textColor) {
      styles.color = customStyles.textColor;
    }
    
    return styles;
  };
  
  // Group menu items by parent for multi-column layout
  const getGroupedMenuItems = () => {
    // Create an appropriate number of columns based on settings
    const numColumns = footerOptions.columns || 4;
    const columns = Array(numColumns).fill().map(() => []);
    
    // Only get top-level items
    const topLevelItems = menuItems.filter(item => !item.parent);
    
    // Distribute items across columns evenly
    topLevelItems.forEach((item, index) => {
      const columnIndex = index % numColumns;
      columns[columnIndex].push(item);
    });
    
    return columns;
  };
  
  // Generate copyright text with current year
  const getCopyrightText = () => {
    const currentYear = new Date().getFullYear();
    const text = footerOptions.copyrightText || '© 2025 Company Name | All Rights Reserved';
    return text.replace('2025', currentYear);
  };
  // If still loading, show minimal footer
  if (menuLoading) {
    return (
      <footer className="bg-gray-900 text-white py-6">
        <div className="container mx-auto px-4 text-center">
          <p>Loading footer...</p>
        </div>
      </footer>
    );
  }
  
  // Define layout based on footer options
  const getFooterLayout = () => {
    const layout = footerOptions.layout || 'standard';
    
    switch (layout) {
      case 'centered':
        return (
          <div className="text-center">
            {footerOptions.logo && (
              <div className="mb-6">
                <img 
                  src={footerOptions.logo} 
                  alt={footerOptions.companyName || 'Company Logo'} 
                  className="mx-auto" 
                  style={{
                    height: footerOptions.logoHeight || '60px',
                    width: footerOptions.logoWidth || 'auto'
                  }}
                />
              </div>
            )}
            
            {footerOptions.companyName && (
              <h4 className="text-xl font-bold mb-2">{footerOptions.companyName}</h4>
            )}
            
            {footerOptions.companyDescription && (
              <p className="text-gray-400 mb-6 max-w-lg mx-auto">{footerOptions.companyDescription}</p>
            )}
            
            {footerOptions.showQuickLinks !== false && menuItems.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                <div className="flex flex-wrap justify-center gap-4">
                  {menuItems.map(item => (
                    <Link key={item._id} to={item.path}
                    //  className="hover:text-blue-500"
                     >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            
            {footerOptions.showContactInfo !== false && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
                {footerOptions.contactPhone && (
                  <p className="flex items-center justify-center">
                    <PhoneIcon className="mr-2 h-5 w-5" />
                    <a href={`tel:${footerOptions.contactPhone}`}>{footerOptions.contactPhone}</a>
                  </p>
                )}
                {footerOptions.contactEmail && (
                  <p className="flex items-center justify-center mt-2">
                    <MailIcon className="mr-2 h-5 w-5" />
                    <a href={`mailto:${footerOptions.contactEmail}`}>{footerOptions.contactEmail}</a>
                  </p>
                )}
                {footerOptions.contactAddress && (
                  <p className="flex items-center justify-center mt-2">
                    <MapPin className="mr-2 h-5 w-5" />
                    {footerOptions.contactAddress}
                  </p>
                )}
              </div>
            )}
            
            {footerOptions.showSocialIcons !== false && footerOptions.socialLinks && footerOptions.socialLinks.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-4">Connect With Us</h4>
                <div className="flex justify-center space-x-4">
                  {footerOptions.socialLinks.map((social, index) => {
                    const SocialIcon = socialIconMap[social.platform] || Globe;
                    return (
                      <a 
                        key={index} 
                        href={social.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-white"
                      >
                        <SocialIcon className="h-6 w-6" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'minimal':
        return (
          <div className="text-center py-4">
            {footerOptions.logo && (
              <div className="mb-4">
                <img 
                  src={footerOptions.logo} 
                  alt={footerOptions.companyName || 'Company Logo'} 
                  className="mx-auto" 
                  style={{
                    height: '60px',
                    width: 'auto'
                  }}
                />
              </div>
            )}
            
            {footerOptions.showSocialIcons !== false && footerOptions.socialLinks && footerOptions.socialLinks.length > 0 && (
              <div className="flex justify-center space-x-4 mb-4">
                {footerOptions.socialLinks.map((social, index) => {
                  const SocialIcon = socialIconMap[social.platform] || Globe;
                  return (
                    <a 
                      key={index} 
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white"
                    >
                      <SocialIcon className="h-5 w-5" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        );
        
      default: // standard
        const columns = getGroupedMenuItems();
        
        return (
          <div className={`grid md:grid-cols-${footerOptions.columns || 4} gap-8`}>
            {/* Company Info Column */}
            <div className="px-8">
              <h4 className="text-xl font-bold mb-4">
                {footerOptions.companyName || 'Company Name'}
              </h4>
              
              {footerOptions.companyDescription && (
                <p className="text-gray-400">
                  {footerOptions.companyDescription}
                </p>
              )}
              
              <div className="mt-4">
                <Link to="/privacy-policy" className="hover:text-white transition">
                  Privacy Policy
                </Link>
                <span> &nbsp;&nbsp; - &nbsp;&nbsp; </span>
                <Link to="/terms-conditions" className="hover:text-white transition">
                  Terms & Conditions
                </Link>
              </div>

              {footerOptions.logo && (
                <div className="mt-4">
                  <img 
                    src={footerOptions.logo} 
                    alt={footerOptions.companyName || 'Company Logo'} 
                    style={{
                      height: footerOptions.logoHeight || '60px',
                      width: footerOptions.logoWidth || 'auto'
                    }}
                  />
                </div>
              )}              
            
            </div>
            
            {/* Menu Item Columns */}
                 
            {footerOptions.showQuickLinks !== false && menuItems.length > 0 && (
              <div className="px-8">
                <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
                <div className="flex flex-col justify-center gap-1">
                  {menuItems.map(item => (
                    <Link key={item._id} to={item.path} 
                    // className="hover:text-blue-500"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {/* {columns.slice(1).map((columnItems, columnIndex) => (
              <div key={columnIndex}>
                {columnItems.length > 0 && columnItems[0] && (
                  <>
                    <h4 className="text-xl font-bold mb-4">{columnItems[0].title || 'Links'}</h4>
                    <ul className="space-y-2">
                      {columnItems.map((item) => (
                        <li key={item._id}>
                          {item.isExternal ? (
                            <a 
                              href={item.path} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="hover:text-blue-500 flex items-center"
                            >
                              {item.icon && renderIcon(item.icon)}
                              {item.title}
                              <ExternalLink className="ml-1 w-3 h-3" />
                            </a>
                          ) : (
                            <Link to={item.path} className="hover:text-blue-500 flex items-center">
                              {item.icon && renderIcon(item.icon)}
                              {item.title}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            ))} */}
            
            {/* Show Contact Info Column */}
            {footerOptions.showContactInfo !== false && (
              <div className="px-8">
                <h4 className="text-xl font-bold mb-4">Contact</h4>
                {footerOptions.contactPhone && (
                  <p className="flex items-center mt-2">
                    <PhoneIcon className="mr-2 h-5 w-5" />
                    <a href={`tel:${footerOptions.contactPhone}`}>{footerOptions.contactPhone}</a>
                  </p>
                )}
                {footerOptions.contactEmail && (
                  <p className="flex items-center mt-2">
                    <MailIcon className="mr-2 h-5 w-5" />
                    <a href={`mailto:${footerOptions.contactEmail}`}>{footerOptions.contactEmail}</a>
                  </p>
                )}
                {footerOptions.contactAddress && (
                  <p className="flex items-center mt-2">
                    <MapPin className="mr-2 h-5 w-5" />
                    {footerOptions.contactAddress}
                  </p>
                )}
              </div>
            )}
            
            {/* Show Social Links Column */}
            {footerOptions.showSocialIcons !== false && footerOptions.socialLinks && footerOptions.socialLinks.length > 0 && (
              <div className="px-8">
                <h4 className="text-xl font-bold mb-4">Follow Us</h4>
                <div className="flex space-x-4">
                  {footerOptions.socialLinks.map((social, index) => {
                    const SocialIcon = socialIconMap[social.platform] || Globe;
                    return (
                      <a 
                        key={index} 
                        href={social.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-white"
                      >
                        <SocialIcon className="h-5 w-5" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
    }
  };
  
  // Function to render icon based on name
  const renderIcon = (iconName) => {
    const iconMap = {
      Home: Home,
      Package: Package,
      Mail: MailIcon,
      Users: Users,
      FileText: FileText,
      MessageCircle: MessageCircle
    };
    
    if (!iconName) return null;
    
    const IconComponent = iconMap[iconName];
    if (IconComponent) {
      return <IconComponent className="w-4 h-4 mr-1" />;
    }
    return null;
  };
  
  return (
    <footer className="bg-gray-900 text-white py-12 pb-0 mb-0" style={getFooterStyle()}>
      <div className="container mx-auto px-4">
        {getFooterLayout()}
        
        {/* Powered By Attribution */}
        <div className="mt-8 border-t border-gray-800 pt-4 pb-6">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center text-center md:text-left">
              <p className="mb-3 md:mb-0">{getCopyrightText()}</p>
              
              {footerOptions.poweredBy?.show !== false && (
                <div className="flex items-center justify-center md:justify-end">
                  {footerOptions.poweredBy && (
                    <img 
                      src="/images/piTech-logo.png"
                      alt=" " 
                      className="inline-block" 
                      style={{ height: '25px', marginRight: '5px', verticalAlign: 'bottom' }} 
                    />
                  )}
                  <a 
                    href={footerOptions.poweredBy?.companyUrl || '#'} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center"
                  >
                    {footerOptions.poweredBy?.text || 'Design & Development By'} {footerOptions.poweredBy?.companyName || 'Your Company'}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;






// // before including desing & coloring & footer
// // src/components/Footer.js
// import React from 'react';
// import { Link } from 'react-router-dom';
// import { InstagramIcon, FacebookIcon, PhoneIcon, MailIcon, LinkedinIcon} from 'lucide-react'; 


// const Footer = () => {
//   const phoneNumber = "+1 (833) 333-3101";

//   return (
//     <footer className="bg-gray-900 text-white py-12 pb-0 mb-0">
//       <div className="container mx-auto px-4 grid md:grid-cols-4 gap-8">
//         <div>
//           <h4 className="text-xl font-bold mb-4">Bag & Box</h4>
//           <p className="text-gray-400">Professional custom packaging solutions for businesses across North America.</p>
//           {/* <img src='../images/logo.png' style={{height:150+"px"}}/> */}
//           <p className='mt-2'>
//           <Link to="/privacy-policy" className="hover:text-white transition">
//             Privacy Policy
//           </Link>
//           <span>
//           &nbsp;&nbsp; - &nbsp;&nbsp;
//           </span>
//           <Link to="/terms-conditions" className="hover:text-white transition">
//             Terms & Conditions
//           </Link></p>
//         </div>
//         <div>
//           <h4 className="text-xl font-bold mb-4">Quick Links</h4>
//           <ul className="space-y-0">
//             <li><Link to="/" className="hover:text-blue-500">Home</Link></li>
//             <li><Link to="/aboutUs" className="hover:text-blue-500">About Us</Link></li>
//             <li><Link to="/blog" className="hover:text-blue-500">Blog</Link></li>
//             <li><Link to="/products" className="hover:text-blue-500">Products</Link></li>
//             <li><Link to="/contactUs" className="hover:text-blue-500">Contact Us</Link></li>
//             <li><Link to="/support" className="hover:text-blue-500">Support</Link></li>
//           </ul>
//         </div>
//         <div>
//           <h4 className="text-xl font-bold mb-4">Contact</h4>
//           <p>
//             {/* ☏ Phone: {phoneNumber} */}
//             {/* {<PhoneIcon className="mr-2 h-5 w-5"/>} */}
//             ☏ Phone: <a href={`tel:${phoneNumber}`} className="">{phoneNumber}</a>

//            </p>
//           <p style={{marginTop:7+'px'}}>
//             ✉ Email: Info@bagbox.ca
//             {/* {<MailIcon className="mr-2 h-5 w-5"/>}  */}
//              </p>
//           <p style={{marginTop:7+'px'}}>
//            ⚲ Address: 102-1127 14th St W,<br/> North Vancouver, BC, V7P 1J9

//           </p>
//         </div>
//         <div>
//           <h4 className="text-xl font-bold mb-4">Follow Us</h4>
//           <div className="flex space-x-4">
//             <a href="https://www.instagram.com/bagbox.ca" className="text-gray-400 hover:text-white">{<InstagramIcon className="mr-2 h-5 w-5"/>}</a>
//             <a href="#" className="text-gray-400 hover:text-white">{<FacebookIcon className="mr-2 h-5 w-5"/>}</a>
//             <a href="#" className="text-gray-400 hover:text-white">{<LinkedinIcon className="mr-2 h-5 w-5"/>}</a>
//           </div>
//         </div>
//       </div>
//       {/* <div className="text-center mt-8 border-t border-gray-800 pt-4 pb-6">
//         <p>&copy; 2025 BagBox.ca | All Rights Reserved.<br/>
//         <img 
//           src="/images/piTech-logo.png" 
//           alt="PiTech Logo" 
//           className="inline-block" 
//           style={{ height: '25px', marginRight: '5px', verticalAlign: 'bottom' }} 
//         />
//         <a href='https://pitech.ca' target='new' className="inline-flex items-center">
//          Design & Development By PiTech.ca</a>
//         </p>
//       </div> */}
//       <div className="mt-8 border-t border-gray-800 pt-4 pb-6">
//         <div className="container mx-auto px-4">
//           <div className="flex flex-col md:flex-row md:justify-between md:items-center text-center md:text-left">
//             <p className="mb-3 md:mb-0">&copy; 2025 BagBox.ca | All Rights Reserved.</p>
//             <div className="flex items-center justify-center md:justify-end">
//               <img 
//                 src="/images/piTech-logo.png" 
//                 alt=" " 
//                 className="inline-block" 
//                 style={{ height: '25px', marginRight: '5px', verticalAlign: 'bottom' }} 
//               />
//               <a href='https://pitech.ca' target='new' className="inline-flex items-center">
//                 Design & Development By PiTech.ca
//               </a>
//             </div>
//           </div>
//         </div>
//       </div>
//     </footer>
//   );
// };

// export default Footer;